import { __decorate, __metadata, __param } from "tslib";
// ref: https://confluence.zhihuiya.com/pages/viewpage.action?pageId=116987797
import _ from 'lodash';
import { inject, injectable } from 'inversify';
import { Symbols } from '@/symbols';
import { E_ResourceStatus, E_ResourceCyclical } from './CompanyPermissionApi.type';
import { format } from '../../@core/utils/string';
import { maybeSerializeArray } from '../../utils/lang';
// #region 前端在api层面 做兼容处理
function descToDescCn(item) {
    const { desc, ...rest } = item;
    return {
        ...rest,
        desc_cn: desc,
    };
}
function descCnToDesc(item) {
    const { desc_cn, ...rest } = item;
    return {
        ...rest,
        desc: desc_cn,
    };
}
function enabledToStatus(item) {
    const { enabled, ...rest } = item;
    return {
        ...rest,
        status: enabled ? E_ResourceStatus.ENABLED : E_ResourceStatus.DISABLED,
    };
}
function statusToEnabled(item) {
    const { status, ...rest } = item;
    return {
        ...rest,
        enabled: status === E_ResourceStatus.ENABLED,
    };
}
function unserializeCyclical(c) {
    if (_.isEmpty(c)) {
        return E_ResourceCyclical.NONE;
    }
    return c;
}
function serializeCyclical(c) {
    if (c === E_ResourceCyclical.NONE) {
        return '';
    }
    return c;
}
// #endregion
let CompanyPermissionApi = class CompanyPermissionApi {
    constructor(http, urls, i18n) {
        this.http = http;
        this.urls = urls;
        this.i18n = i18n;
        // #region
        this.getProductResourceList = (product_id) => {
            const url = format(this.urls.getProductResourceList, {
                product_id,
            });
            return this.http.get({
                url,
            });
        };
        // #endregion
        // #region Resource
        /**
         * 获取容量资源信息列表
         */
        this.getResourceList = (params) => {
            const url = this.urls.getResourceList;
            params = maybeSerializeArray(params, 'product_ids');
            return this.http.get({
                url,
                params,
            });
        };
        /**
         * 查询可定制化的资源
         */
        this.getResourceCustomizedList = () => {
            const url = this.urls.getResourceCustomizedList;
            return this.http.get({
                url,
            });
        };
        /**
         * 创建容量资源信息
         */
        this.postResourceCreate = (params) => {
            const url = this.urls.postResourceCreate;
            return this.http.post({
                url,
                data: params,
            });
        };
        /**
         * 编辑容量资源信息
         */
        this.putResourceEdit = (params) => {
            const url = this.urls.putResourceEdit;
            return this.http.put({
                url,
                data: params,
            });
        };
        // #endregion
        // #region Service
        this.getService = (service_id) => {
            const url = format(this.urls.getService, {
                service_id,
            });
            return this.http.get({
                url,
            }).then(item => {
                item = enabledToStatus(item);
                item = descToDescCn(item);
                return item;
            });
        };
        this.getServiceList = (params) => {
            const url = this.urls.getServiceList;
            if (params?.status) {
                params = statusToEnabled(params);
            }
            return this.http.get({
                url,
                params,
            }).then(res => {
                const unserializeCapacityList = (lst) => _.map(lst, item => {
                    return {
                        ...item,
                        cyclical_type: unserializeCyclical(item.cyclical_type),
                    };
                });
                return {
                    ...res,
                    aaData: _.map(res.aaData, item => {
                        item = enabledToStatus(item);
                        item = descToDescCn(item);
                        if (item.capacity_list) {
                            item = {
                                ...item,
                                capacity_list: unserializeCapacityList(item.capacity_list),
                            };
                        }
                        if (item.performance_limitation_list) {
                            item = {
                                ...item,
                                performance_limitation_list: unserializeCapacityList(item.performance_limitation_list),
                            };
                        }
                        /** region 产品功能 */
                        const feature = item.feature_role;
                        if (item.feature_role) {
                            const feature_role_display = (this.i18n.locale === 'cn' ? feature.role_name_cn : feature.role_name_us) || feature.role_name;
                            item.feature_role_display = feature_role_display;
                            item.feature_role_id = feature.role_id;
                        }
                        else {
                            item.feature_role_display = '-';
                        }
                        /** region end */
                        return item;
                    }),
                };
            });
        };
        this.postServiceCreate = (params) => {
            const url = this.urls.postServiceCreate;
            params = descCnToDesc(params);
            params = statusToEnabled(params);
            return this.http.post({
                url,
                data: params,
            });
        };
        this.putServiceEdit = (params) => {
            const url = this.urls.putServiceEdit;
            params = descCnToDesc(params);
            params = statusToEnabled(params);
            return this.http.put({
                url,
                data: params,
            });
        };
        this.putServicePriceEdit = (params) => {
            const url = this.urls.putServicePriceEdit;
            return this.http.put({
                url,
                data: params,
            });
        };
        this.postServiceClone = (params) => {
            const url = this.urls.postServiceClone;
            params = descCnToDesc(params);
            params = statusToEnabled(params);
            return this.http.post({
                url,
                data: params,
            });
        };
        /**
         * 容量列表
         */
        this.getServiceResourceLimitationsList = (params) => {
            const url = this.urls.getServiceResourceLimitationsList;
            return this.http.get({
                url,
                params,
            }).then(res => _.map(res, item => {
                return {
                    ...item,
                    cyclical_type: unserializeCyclical(item.cyclical_type),
                };
            }));
        };
        /**
         * 容量添加
         */
        this.postServiceResourceLimitationsCreate = (params) => {
            const url = this.urls.postServiceResourceLimitationsCreate;
            params = {
                ...params,
                add_limitation_list: _.map(params.add_limitation_list, item => {
                    return {
                        ...item,
                        cyclical_type: serializeCyclical(item.cyclical_type),
                    };
                }),
            };
            return this.http.post({
                url,
                data: params,
            });
        };
        /**
         * 容量编辑
         */
        this.putServiceResourceLimitationsEdit = (params) => {
            const url = this.urls.putServiceResourceLimitationsEdit;
            params = {
                ...params,
                update_limitation_list: _.map(params.update_limitation_list, item => {
                    return {
                        ...item,
                        cyclical_type: serializeCyclical(item.cyclical_type),
                    };
                }),
            };
            return this.http.put({
                url,
                data: params,
            });
        };
        /**
         * 容量删除
         */
        this.putServiceResourceLimitationsDelete = (params) => {
            const url = this.urls.putServiceResourceLimitationsDelete;
            return this.http.put({
                url,
                data: params,
            });
        };
        /**
         * Performance添加
         */
        this.postServicePerformanceLimitationsCreate = (params) => {
            const url = this.urls.postServicePerformanceLimitationsCreate;
            params = {
                ...params,
                add_limitation_list: _.map(params.add_limitation_list, item => {
                    return {
                        ...item,
                        cyclical_type: serializeCyclical(item.cyclical_type),
                    };
                }),
            };
            return this.http.post({
                url,
                data: params,
            });
        };
        /**
         * Performance编辑
         */
        this.putServicePerformanceLimitationsEdit = (params) => {
            const url = this.urls.putServicePerformanceLimitationsEdit;
            params = {
                ...params,
                update_limitation_list: _.map(params.update_limitation_list, item => {
                    return {
                        ...item,
                        cyclical_type: serializeCyclical(item.cyclical_type),
                    };
                }),
            };
            return this.http.put({
                url,
                data: params,
            });
        };
        /**
         * Performance删除
         */
        this.putServicePerformanceLimitationsDelete = (params) => {
            const url = this.urls.putServicePerformanceLimitationsDelete;
            return this.http.put({
                url,
                data: params,
            });
        };
        // #endregion
        // #region Capacity
        this.getCapacityList = (params) => {
            const url = this.urls.getCapacityList;
            params = maybeSerializeArray(params, 'product_ids', 'capacity_resourceIds');
            return this.http.get({
                url,
                params,
            });
        };
        this.postCapacityCreate = (params) => {
            const url = this.urls.postCapacityCreate;
            params = {
                ...params,
                add_capacity_list: _.map(params.add_capacity_list, item => {
                    item = {
                        ...item,
                        cyclical_type: serializeCyclical(item.cyclical_type),
                    };
                    return item;
                }),
            };
            return this.http.post({
                url,
                data: params,
            });
        };
        this.putCapacityEdit = (params) => {
            const url = this.urls.putCapacityEdit;
            return this.http.put({
                url,
                data: params,
            });
        };
        /** 根据功能 查询 容量容量 */
        this.getCapacityResouceByFeature = (feature_role_id) => {
            const url = this.urls.getCapacityResouceByFeature;
            return this.http.get({
                url,
                params: { feature_role_id },
            });
        };
    }
};
CompanyPermissionApi = __decorate([
    injectable(),
    __param(0, inject(Symbols.BoHttp)),
    __param(1, inject(Symbols.CompanyPermissionApiUrls)),
    __param(2, inject(Symbols.I18n)),
    __metadata("design:paramtypes", [Object, Object, Object])
], CompanyPermissionApi);
export { CompanyPermissionApi };
